import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAccess } from "@/lib/access";
import VendorFormDialog from "@/components/VendorFormDialog";
import VendorStatusBadge from "@/components/vendor/VendorStatusBadge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Plus,
  Storefront,
  CurrencyInr,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["Started", "In-Progress", "Partial", "Completed", "AMC"];

function formatINR(v) {
  return `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function Vendors() {
  const { can } = useAccess();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors", {
        params: { search: search || undefined, status },
      });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleCreate = async (payload) => {
    try {
      const { data } = await api.post("/vendors", payload);
      setShowForm(false);
      toast.success("Vendor created");
      navigate(`/inventory/vendors/${data.id}`);
    } catch {
      toast.error("Failed to create vendor");
    }
  };

  const totalPayments = rows.reduce(
    (s, r) => s + Number(r.total_payments || 0),
    0
  );
  const totalActual = rows.reduce((s, r) => s + Number(r.actual_value || 0), 0);

  return (
    <div data-testid="vendors-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Operations
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Vendors
            </h1>
          </div>
          {can("vendor.edit") && <button
            onClick={() => setShowForm(true)}
            data-testid="new-vendor-button"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} weight="bold" /> New Vendor
          </button>}
        </div>
      </div>

      <div className="p-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Total Vendors
            </div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">
              {rows.length}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Total Payments
            </div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">
              {formatINR(totalPayments)}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Total Actual Value
            </div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">
              {formatINR(totalActual)}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              data-testid="vendor-search-input"
              placeholder="Search by vendor name, purpose, or PoC…"
              className="pl-9 h-10 bg-white border-slate-200"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              className="w-full sm:w-56 h-10 bg-white border-slate-200"
              data-testid="vendor-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vendors-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  <th className="px-6 py-3">Vendor</th>
                  <th className="px-6 py-3">Purpose</th>
                  <th className="px-6 py-3">Actual Value</th>
                  <th className="px-6 py-3">Payments</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                      No vendors yet. Click &ldquo;New Vendor&rdquo; to add one.
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((v) => {
                    const balance =
                      Number(v.actual_value || 0) - Number(v.total_payments || 0);
                    return (
                      <tr
                        key={v.id}
                        onClick={() => navigate(`/inventory/vendors/${v.id}`)}
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        data-testid={`vendor-row-${v.id}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-slate-100 rounded-md flex items-center justify-center text-slate-500">
                              <Storefront size={18} weight="duotone" />
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">{v.name}</div>
                              {v.pocs?.length > 0 && (
                                <div className="text-xs text-slate-500">
                                  {v.pocs[0].name}
                                  {v.pocs.length > 1 && ` + ${v.pocs.length - 1} more`}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-700 max-w-md truncate">
                          {v.purpose || "—"}
                        </td>
                        <td className="px-6 py-4 text-slate-800 font-medium">
                          {v.actual_value > 0 ? (
                            formatINR(v.actual_value)
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-800">
                            {formatINR(v.total_payments)}
                          </div>
                          {v.actual_value > 0 && balance > 0 && (
                            <div className="text-xs text-amber-600">
                              {formatINR(balance)} pending
                            </div>
                          )}
                          {v.actual_value > 0 && balance <= 0 && v.total_payments > 0 && (
                            <div className="text-xs text-emerald-600">Paid</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <VendorStatusBadge status={v.status} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showForm && (
        <VendorFormDialog
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}
